import React, { ReactNode } from "react";
import { Box, Button, Chip, Link, Stack, Typography } from "@mui/material";

import { BG_COLORS, BORDER_COLORS } from "../../../constants";
import { TAsinCheckStatus } from "../../../services/apiV2.types";

export const STATUS_LABEL: Record<TAsinCheckStatus, { label: string; bg: string }> = {
  ungated: { label: "Ungated", bg: BG_COLORS.success },
  approval_required: { label: "Approval required", bg: BG_COLORS.warning },
  not_eligible: { label: "Not eligible", bg: "#FDE2E2" },
  not_found: { label: "ASIN not found", bg: "#EEEEEE" },
  not_checked: { label: "Not checked", bg: "#EEEEEE" },
  error: { label: "Couldn't check", bg: "#EEEEEE" },
};

type Props = {
  testId: string;
  asin: string;
  brand?: string | null;
  title?: string | null;
  imageUrl?: string | null;
  status: TAsinCheckStatus;
  approvalLink?: string | null;
  message?: string | null;
  /** Small chips next to the brand name, e.g. "New" or "Your check". */
  badges?: ReactNode;
  /** Extra text after the ASIN and title, e.g. "checked 2 days ago". */
  meta?: string;
  /** Extra actions after "Apply to sell", e.g. "I applied". */
  actions?: ReactNode;
};

/** One product row: image, brand, ASIN · title, status chip and actions. Used by both result lists. */
export const ResultRow: React.FC<Props> = ({
  testId,
  asin,
  brand,
  title,
  imageUrl,
  status,
  approvalLink,
  message,
  badges,
  meta,
  actions,
}) => {
  const chip = STATUS_LABEL[status] ?? STATUS_LABEL.error;
  const details = [title, meta].filter(Boolean).join(" · ");

  return (
    <Stack
      data-testid={testId}
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      gap="12px"
      padding="10px 12px"
      sx={{
        borderBottom: `1px solid ${BORDER_COLORS.main}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt=""
          width={40}
          height={40}
          sx={{ objectFit: "contain", flexShrink: 0 }}
        />
      ) : (
        <Box width={40} height={40} flexShrink={0} />
      )}
      <Box minWidth={0} flex={1}>
        <Stack direction="row" alignItems="center" gap="8px" minWidth={0}>
          <Typography fontWeight={600} fontSize="14px" noWrap>
            {brand || "Unknown brand"}
          </Typography>
          {badges}
        </Stack>
        <Typography fontSize="12px" color="text.secondary" noWrap>
          <Link
            href={`https://www.amazon.com/dp/${asin}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {asin}
          </Link>
          {details ? ` · ${details}` : ""}
        </Typography>
        {message && status !== "ungated" && status !== "approval_required" && (
          <Typography fontSize="12px" color="text.secondary">
            {message}
          </Typography>
        )}
      </Box>
      <Chip
        label={chip.label}
        size="small"
        sx={{ bgcolor: chip.bg, fontWeight: 600 }}
      />
      {(status === "approval_required" && approvalLink) || actions ? (
        <Stack direction="row" gap="8px" flexShrink={0} alignItems="center">
          {status === "approval_required" && approvalLink && (
            <Button
              size="small"
              variant="contained"
              disableElevation
              href={approvalLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Apply to sell
            </Button>
          )}
          {actions}
        </Stack>
      ) : null}
    </Stack>
  );
};

export default ResultRow;
